import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue, getLatestSubmission, submissionNeedsReview } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { resolveDocTemplate } from '../../core/seed-doc-templates';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon, InlineIcon } from '../../components/Icon';
import { DocActions } from '../../components/DocActions';
import { CustomFieldInputs, CustomFieldDisplay } from '../../components/CustomFieldInputs';


function SubmissionViewer({ sub, tpl, task, onReview }) {
  const { showToast } = useApp();
  const statusColors = { submitted:"#7C3AED", approved:"#16A34A", changes_requested:"#DC2626" };
  const sc = statusColors[sub.status] || "#6B7280";

  return (
    <div>
      {/* Status banner */}
      <div style={{ padding:"12px 14px", background:sub.status==="submitted"?"#EDE9FE":sub.status==="approved"?"#F0FDF4":"#FFF1F2", border:`1px solid ${sub.status==="submitted"?"#C4B5FD":sub.status==="approved"?"#BBF7D0":"#FECACA"}`, borderRadius:8, marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:sc }}>
            {sub.status==="submitted"
              ? <InlineIcon name="review" size={13} gap={5}>Pending Review</InlineIcon>
              : sub.status==="approved"
                ? <InlineIcon name="check" size={13} gap={5}>Approved</InlineIcon>
                : <InlineIcon name="undo" size={13} gap={5}>Changes Requested</InlineIcon>}
          </div>
          <div style={{ fontSize:11, color:"var(--muted)", marginTop:2 }}>Submitted on {sub.submittedAt}</div>
        </div>
        {sub.status==="submitted" && (
          <button className="btn btn-sm" style={{ background:"#7C3AED",color:"#fff",border:"none" }} onClick={onReview}>Review Now →</button>
        )}
      </div>

      {sub.reviewNote && (
        <div style={{ padding:"10px 14px", background:"#FFF1F2", border:"1px solid #FECACA", borderRadius:8, fontSize:12, color:"var(--red)", marginBottom:16 }}>
          <strong>Changes requested:</strong> {sub.reviewNote}
        </div>
      )}

      {/* Render per-director or company sections */}
      {sub.formData && Object.keys(sub.formData).map(dirIdx => {
        const dirLabel = tpl?.collectionType==="director" ? `Director ${dirIdx}` : "Company Information";
        const fields   = sub.formData[dirIdx];
        const docs     = sub.documents?.[dirIdx];

        return (
          <div key={dirIdx} style={{ border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", marginBottom:14 }}>
            <div style={{ padding:"10px 16px", background:"linear-gradient(90deg,#F0F6FF,#fff)", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:"var(--blue)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff" }}>
                {tpl?.collectionType==="director" ? dirIdx : <Icon name="company" size={14} color="#fff" />}
              </div>
              <div style={{ fontSize:13, fontWeight:700 }}>{dirLabel}</div>
            </div>

            {/* Info fields */}
            {fields && Object.keys(fields).length>0 && (
              <div style={{ padding:"12px 16px", borderBottom:docs&&Object.keys(docs).length>0?"1px solid var(--border)":undefined }}>
                <div style={{ fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10 }}><InlineIcon name="clipboard" size={12} gap={5}>Submitted Information</InlineIcon></div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 16px" }}>
                  {tpl?.infoFields?.map(f=>(
                    <div key={f.id} style={{ gridColumn:f.type==="textarea"?"1/-1":undefined }}>
                      <div style={{ fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:2 }}>{f.label}</div>
                      <div style={{ fontSize:13,fontWeight:500,padding:"6px 10px",background:"#F9FAFB",borderRadius:6,border:"1px solid var(--border)" }}>
                        {fields[f.id] || <span style={{ color:"var(--faint)",fontStyle:"italic" }}>Not filled</span>}
                      </div>
                    </div>
                  )) || Object.entries(fields).map(([k,v])=>(
                    <div key={k}>
                      <div style={{ fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:2 }}>{k}</div>
                      <div style={{ fontSize:13,fontWeight:500,padding:"6px 10px",background:"#F9FAFB",borderRadius:6,border:"1px solid var(--border)" }}>{v||"—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            {docs && Object.keys(docs).length>0 && (
              <div style={{ padding:"12px 16px" }}>
                <div style={{ fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10 }}><InlineIcon name="file" size={12} gap={5}>Uploaded Documents</InlineIcon></div>
                {tpl?.docSlots?.map(slot=>{
                  const up = docs[slot.id];
                  return (
                    <div key={slot.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #F3F4F6" }}>
                      <div style={{ width:30,height:30,borderRadius:7,background:up?"#F0FDF4":"#FFF1F2",border:`1px solid ${up?"#BBF7D0":"#FECACA"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14 }}>
                        <Icon name={up ? "check" : "close"} size={15} color={up ? "var(--green)" : "var(--red)"} />
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13,fontWeight:500 }}>{slot.label}{slot.required&&<span style={{ color:"var(--red)",marginLeft:3 }}>*</span>}</div>
                        {up ? <div style={{ fontSize:11,color:"var(--green)" }}><InlineIcon name="check" size={11} gap={4}>{up.name} · {up.size}</InlineIcon></div>
                             : <div style={{ fontSize:11,color:"var(--red)" }}>Not uploaded</div>}
                      </div>
                      {up && (
                        <DocActions doc={up} name={up.name || slot.label} onError={(msg) => showToast(msg, "error")} compact />
                      )}
                    </div>
                  );
                }) || Object.entries(docs).map(([slotId,up])=>(
                  <div key={slotId} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #F3F4F6" }}>
                    <Icon name="check" size={16} color="var(--green)" />
                    <div style={{ flex:1 }}><div style={{ fontSize:13,fontWeight:500 }}>{up.name}</div><div style={{ fontSize:11,color:"var(--muted)" }}>{up.size}</div></div>
                    {up ? <DocActions doc={up} name={up.name} onError={(msg) => showToast(msg, "error")} compact /> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {sub.note && (
        <div style={{ padding:"10px 14px",background:"#F9FAFB",border:"1px solid var(--border)",borderRadius:8,fontSize:12,color:"var(--muted)" }}>
          <Icon name="message" size={13} /> <strong>Client note:</strong> {sub.note}
        </div>
      )}
    </div>
  );
}

// ─── Create Employee Modal ────────────────────────────────────────────


function ViewTaskModal({ data, onClose }) {
  const { task } = data;
  const { tasks, setTasks, employees, showToast, user, submissions, openModal, docTpls, org, can } = useApp();
  const [localTask, setLocal] = useState({ ...task, comments: task.comments || [] });
  const [activeTab, setActiveTab] = useState("details");
  const [newComment, setNewComment] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [submittingDelete, setSubmittingDelete] = useState(false);
  const isTeam = user?.role === "admin" || user?.role === "manager" || user?.role === "employee";
  const taskCustomFields = org?.customFields?.tasks || [];

  const submitDeleteRequest = async () => {
    setSubmittingDelete(true);
    try {
      await sb.createDeleteRequest({
        entity_type: "tasks",
        entity_id: task.id,
        reason: deleteReason.trim(),
        company_id: task.clientId,
        reassign_to: null,
      });
      showToast("Delete request submitted — awaiting admin approval", "success");
      setConfirmDelete(false);
      setDeleteReason("");
      onClose();
    } catch (err) {
      showToast(err?.message || "Failed to submit delete request", "error");
    } finally {
      setSubmittingDelete(false);
    }
  };

  const sub = getLatestSubmission(submissions, task.id);
  const tpl = resolveDocTemplate(task.docTemplateId, docTpls);

  const addComment = () => {
    const text = newComment.trim();
    if (!text) return;
    const entry = {
      id: uuid(),
      userId: user.id,
      userName: user.name,
      role: user.role,
      text,
      at: new Date().toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }),
    };
    setLocal(t => ({ ...t, comments: [...(t.comments || []), entry] }));
    setNewComment("");
  };

  const save = async () => {
    const payload = {
      status: localTask.status,
      assigned_to: localTask.assignedTo,
      due_date: localTask.dueDate || null,
      notes: localTask.notes || "",
      requirement_type: localTask.requirementType,
      comments: localTask.comments || [],
      custom_data: localTask.customData || {},
      completed_date: localTask.status === "completed" ? (localTask.completedDate || today()) : null,
      updated_at: new Date().toISOString(),
    };
    await sb.update("tasks", "id", task.id, payload).catch(() => {});
    setTasks(ts => ts.map(t => t.id === task.id
      ? { ...localTask, completedDate: localTask.status === "completed" ? (localTask.completedDate || today()) : localTask.completedDate }
      : t
    ));
    showToast("Task updated!", "success");
    onClose();
  };

  return (
    <div className="modal-box" style={{ maxWidth:640 }}>
      <div className="modal-head">
        <div>
          <div className="modal-title">{localTask.title}</div>
          <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{localTask.clientName} · Sequence #{localTask.sequence}</div>
        </div>
        <button className="modal-close" onClick={onClose}><Icon name="close" size={16} /></button>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:2, padding:"0 20px", borderBottom:"1px solid var(--border)", background:"#FAFAF8" }}>
        {[["details","Details"], ...(isTeam&&sub ? [["submission",<InlineIcon key="submission-label" name="review" size={13} gap={5}>Submission</InlineIcon>]] : [])].map(([id,label])=>(
          <div key={id} onClick={()=>setActiveTab(id)} style={{ padding:"10px 14px", fontSize:13, fontWeight:activeTab===id?700:500, cursor:"pointer", color:activeTab===id?"var(--blue)":"var(--muted)", borderBottom:activeTab===id?"2px solid var(--blue)":"2px solid transparent", marginBottom:-1 }}>
            {label}
            {id==="submission"&&sub?.status==="submitted"&&<span style={{ marginLeft:6, background:"#7C3AED", color:"#fff", fontSize:9, padding:"1px 6px", borderRadius:20, fontWeight:700 }}>NEW</span>}
          </div>
        ))}
      </div>

      <div className="modal-body">
        {activeTab==="details" && (
          <div className="form-grid">
            <div className="form-grid-2">
              <div className="f-group">
                <label className="f-label">Status</label>
                <select className="f-select" value={localTask.status} onChange={e=>setLocal(t=>({...t,status:e.target.value}))}>
                  {TASK_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">Assigned To</label>
                <select className="f-select" value={localTask.assignedTo} onChange={e=>setLocal(t=>({...t,assignedTo:e.target.value}))}>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="f-group">
                <label className="f-label">Due Date</label>
                <input type="date" className="f-input" value={localTask.dueDate||""} onChange={e=>setLocal(t=>({...t,dueDate:e.target.value}))} />
              </div>
            </div>

            {/* Document template info */}
            {tpl && (
              <div style={{ padding:"12px 14px", background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:8 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#0369A1", marginBottom:4 }}><InlineIcon name="clipboard" size={13} gap={5}>Configured Template: {tpl.name}</InlineIcon></div>
                <div style={{ fontSize:11, color:"#0369A1" }}>
                  Collection: <strong>{tpl.collectionType==="director"?"Per Director":"Per Company"}</strong>
                  {" · "}{tpl.infoFields.length} info fields
                  {" · "}{tpl.docSlots.length} document slots
                  {" · "}{tpl.docSlots.filter(s=>s.required).length} required docs
                </div>
              </div>
            )}

            {/* Client action required section */}
            <div style={{ borderTop:"1px solid var(--border)", paddingTop:16 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>Client Action Required</div>
              <div className="f-group">
                <label className="f-label">Requirement Type</label>
                <select className="f-select" value={localTask.requirementType||"none"} onChange={e=>setLocal(t=>({...t,requirementType:e.target.value}))}>
                  {TASK_REQUIREMENT_TYPES.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              {!tpl && (localTask.requirementType==="docs"||localTask.requirementType==="form_docs") && (
                <div className="f-group" style={{ marginTop:8 }}>
                  <label className="f-label">Required Documents (one per line)</label>
                  <textarea className="f-textarea" rows={4}
                    placeholder={"PAN Card\nAadhaar Card\nPhoto\nAddress Proof"}
                    value={localTask.docsList||""}
                    onChange={e=>setLocal(t=>({...t,docsList:e.target.value}))}
                  />
                  <div className="f-hint">Or use a Document Template from Settings → Document Templates</div>
                </div>
              )}
            </div>

            <div className="f-group">
              <label className="f-label">Internal Notes (team only)</label>
              <textarea className="f-textarea" value={localTask.notes||""} onChange={e=>setLocal(t=>({...t,notes:e.target.value}))} rows={3} placeholder="Notes visible to team only…" />
            </div>

            <CustomFieldInputs
              fields={taskCustomFields}
              values={localTask.customData || {}}
              onChange={v => setLocal(t => ({ ...t, customData: v }))}
            />

            <div style={{ borderTop:"1px solid var(--border)", paddingTop:16, marginTop:8 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:10 }}>Team Notes {isTeam ? "" : "(visible to you)"}</div>
              {(localTask.comments || []).length === 0 && (
                <div style={{ fontSize:12, color:"var(--muted)", marginBottom:10 }}>No notes yet.</div>
              )}
              {(localTask.comments || []).map(c => (
                <div key={c.id} style={{ padding:"10px 12px", background:"#F9FAFB", borderRadius:8, marginBottom:8, border:"1px solid var(--border)" }}>
                  <div style={{ fontSize:11, color:"var(--muted)", marginBottom:4 }}>{c.userName} · {c.at}</div>
                  <div style={{ fontSize:13 }}>{c.text}</div>
                </div>
              ))}
              {isTeam && (
                <div style={{ display:"flex", gap:8, marginTop:8 }}>
                  <textarea className="f-textarea" rows={2} style={{ flex:1 }} placeholder="Add a note for the client and team…" value={newComment} onChange={e=>setNewComment(e.target.value)} />
                  <button type="button" className="btn btn-primary btn-sm" onClick={addComment}>Add</button>
                </div>
              )}
            </div>

            {localTask.completedDate && (
              <div style={{ fontSize:12, color:"var(--green)", fontWeight:600 }}><InlineIcon name="check" size={13} gap={5}>Completed on {localTask.completedDate}</InlineIcon></div>
            )}
          </div>
        )}

        {activeTab==="submission" && sub && (
          <SubmissionViewer sub={sub} tpl={tpl} task={task} onReview={()=>{ onClose(); openModal("review-submission",{submissionId:sub.id}); }} />
        )}
      </div>

      {confirmDelete && (
        <div className="dr-confirm-form" style={{ padding:"12px 20px", background:"#FEF2F2", borderTop:"1px solid #FCA5A5" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#991B1B", marginBottom:4 }}>Delete "{task.title}"?</div>
          <div style={{ fontSize:11, color:"#991B1B", marginBottom:8 }}>This requires admin approval before taking effect.</div>
          <div className="f-group" style={{ marginBottom:8 }}>
            <label className="f-label">Reason for deletion (optional)</label>
            <textarea className="f-textarea" rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} placeholder="Add a reason for this deletion request…" />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-sm" onClick={()=>{ setConfirmDelete(false); setDeleteReason(""); }} disabled={submittingDelete}>Cancel</button>
            <button className="btn btn-sm btn-red" onClick={submitDeleteRequest} disabled={submittingDelete}>
              <InlineIcon name="trash" size={13} gap={5}>{submittingDelete ? "Submitting…" : "Submit Delete Request"}</InlineIcon>
            </button>
          </div>
        </div>
      )}
      <div className="modal-foot">
        {isTeam && can && can("tasks","delete") && !confirmDelete && (
          <button className="btn btn-sm" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5",marginRight:"auto"}}
            onClick={()=>{ setConfirmDelete(true); setDeleteReason(""); }}>
            <InlineIcon name="trash" size={14} gap={5}>Delete Task</InlineIcon>
          </button>
        )}
        <button className="btn" onClick={onClose}>Cancel</button>
        {activeTab==="submission" && sub?.status==="submitted" && (
          <button className="btn" style={{ background:"#7C3AED",color:"#fff",border:"none" }}
            onClick={()=>{ onClose(); openModal("review-submission",{submissionId:sub.id}); }}>
            <InlineIcon name="review" size={14} gap={5}>Review Submission</InlineIcon>
          </button>
        )}
        {activeTab==="details" && (
          <>
            <button className="btn btn-green" onClick={()=>{ setLocal(t=>({...t,status:"completed"})); setTimeout(save,100); }}><InlineIcon name="check" size={13} gap={5}>Mark Complete</InlineIcon></button>
            <button className="btn btn-primary" onClick={save}>Save Changes</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── SUBMISSION VIEWER — shows submitted data cleanly to team ─────────



export { ViewTaskModal, SubmissionViewer };
